import React from "react";
import { NavLink } from "react-router-dom";
import useTranslation from "../../../i18n/useTranslation";

export default function OrgPortalFooter({ branding, theme, imgPath = "/dist/img/" }) {
    const {
        primaryColor,
        textOnPrimary,
        surfaceColor,      // optional: used for subtle chip/backgrounds
    } = theme;
    const {t} = useTranslation();

    const year = new Date().getFullYear();

    const css = `
    .org-footer {
      margin-top: 40px;
      padding: 18px 0;
      font-size: 13px;
    }

    .org-footer__inner {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 20px;
      flex-wrap: wrap;
    }

    .org-footer__brand,
    .org-footer__center,
    .org-footer__powered {
      flex: 1 1 260px;
    }

    /* left column */
    .org-footer__brand {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    }

    .org-footer__brand-text {
      min-width: 0;
    }

    .org-footer__brand-name {
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .org-footer__brand-sub {
      font-size: 11px;
      opacity: 0.85;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* center column */
    .org-footer__center {
      text-align: center;
    }

    .org-footer__link {
      text-decoration: underline;
      font-weight: 500;
    }

    .org-footer__contact {
      margin: 6px 0 4px;
      font-size: 12px;
    }

    .org-footer__email {
      text-decoration: underline;
      font-weight: 500;
    }

    .org-footer__legal {
      font-size: 11px;
      opacity: 0.9;
      line-height: 1.4;
    }

    /* right column */
    .org-footer__powered {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 6px;
    }

    .org-footer__kbola {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .org-footer__kbola-name {
      font-weight: 600;
      font-size: 12px;
      letter-spacing: 0.3px;
    }

    .org-footer__powered-link {
      display: flex;
      align-items: center;
      gap: 8px;
      text-decoration: none;
      font-size: 11px;
      opacity: 0.95;
    }

    /* logos */
    .org-footer__logo {
      max-width: 100%;
      height: 26px;
      object-fit: contain;
    }

    .org-footer__logo--kbola {
      background: #ffffff;
      border-radius: 10px;
      box-sizing: border-box;
      padding: 5px;
    }

    .org-footer__logo--org {
      height: 40px;
      border-radius: 8px;
      padding: 4px;
      /* background handled inline via theme colors */
    }

    /* ====== SMALL DEVICES ====== */
    @media (max-width: 768px) {
      .org-footer__inner {
        flex-direction: column;
        align-items: center;
        text-align: center;
      }

      .org-footer__brand,
      .org-footer__center,
      .org-footer__powered {
        flex: 1 1 auto;
        align-items: center;
        justify-content: center;
      }

      .org-footer__powered {
        margin-top: 4px;
      }
    }

    /* EXTRA SMALL phones */
    @media (max-width: 480px) {
      .org-footer {
        padding: 14px 0;
      }

      .org-footer__logo--org {
        height: 32px;
      }

      .org-footer__logo {
        height: 22px;
      }
    }
  `;

    return (
        <>
            {/* Local styles for this component */}
                <style>{css}</style>

                <footer
                    className="org-footer"
                    style={{
                        // 🔹 Global footer background = primaryColor
                        backgroundColor: primaryColor,
                        color: textOnPrimary,
                    }}
                >
                    <div className="org-footer__inner">
                        {/* Left: Organization brand */}
                        <div className="org-footer__brand">
                            {branding?.logoUrl && (
                                <img
                                    src={branding.logoUrl}
                                    alt={branding.organization.name}
                                    className="org-footer__logo org-footer__logo--org"
                                    style={{
                                        height: 80,
                                        // 🔹 Chip behind org logo: soft overlay using theme
                                        backgroundColor:
                                            surfaceColor
                                                ? `${textOnPrimary}1F` // ~12% overlay over primary
                                                : "rgba(255,255,255,0.12)",
                                        border: "1px solid rgba(255,255,255,0.25)",
                                    }}
                                />
                            )}
                            <div className="org-footer__brand-text">
                                <div className="org-footer__brand-name">
                                    {branding.organization?.name || "Organization"}
                                </div>
                                <div className="org-footer__brand-sub">
                                    {t('home.footer.brandTitle')}
                                </div>
                            </div>
                        </div>

                        {/* Center: Links & contact */}
                        <div className="org-footer__center">
                            <NavLink
                                to="/terms"
                                className="org-footer__link"
                                style={{ color: textOnPrimary }}
                            >
                                {t('home.footer.terms')}
                            </NavLink>

                            <p className="org-footer__contact">
                                {t('home.footer.contact')}:{" "}
                                <a
                                    href="mailto:kbolasports@angeonx.com"
                                    className="org-footer__email"
                                    style={{ color: textOnPrimary }}
                                >
                                    kbolasports@angeonx.com
                                </a>
                            </p>

                            <div className="org-footer__legal">
                                {t('home.footer.rights', {year})}
                                <br />
                                {t('home.footer.angeonx')}
                            </div>
                        </div>

                        {/* Right: Powered by AngeonX Sports / AngeonX */}
                        <div className="org-footer__powered">
                            <div className="org-footer__kbola">
                                <a
                                    href="https://www.angeonx.com"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="org-footer__powered-link"
                                    style={{ color: textOnPrimary }}
                                >
                                <span className="org-footer__powered-label">
                                    {t('home.footer.poweredBy')}
                                </span>
                                    <img
                                        src={`${imgPath}angeonx-sports.png`}
                                        alt="AngeonX Sports"
                                        className="org-footer__logo org-footer__logo--kbola"
                                        style={{ height: 50 }}
                                    />
                                    <img
                                        style={{ height: 100 }}
                                        src={`${imgPath}angeonx-white.png`}
                                        alt="AngeonX Inc."
                                        className="org-footer__logo org-footer__logo--angeonx"
                                    />
                                </a>
                            </div>
                        </div>
                    </div>
                </footer>
        </>
    );
}
